import {
  mergeAttributes,
  Node,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { buildMergeTagInner, parseMergeTagInner } from "@/lib/merge-tag-format";
import { DynamicFieldChip } from "@/modules/flow/editor/dynamic-field-chip";

/**
 * Inline atomic Tiptap node representing an inserted dynamic field. It renders
 * as a {@link DynamicFieldChip} but serializes back to the `{path}` merge tag —
 * as plain text via `getText()` (SMS) and as a `<span data-merge-tag>` carrying
 * the literal `{path}` via `getHTML()` (email blocks). Either way the stored
 * value keeps the `{path}` token, so the server merge-tag contract is unchanged.
 *
 * By default the node renders the pure {@link DynamicFieldChip} (curated-label
 * fallback, no data fetching), so it is safe in any editor. A host can inject a
 * richer chip — e.g. one that resolves the field type and shows example values
 * on hover — by wrapping the editor in {@link MergeTagChipProvider}. The flow
 * SMS editor uses this to render trigger-scoped chips.
 */

const TOKEN_REGEX = /\{([^{}]+)\}/g;

/**
 * Extra context handed to a merge-tag chip renderer so a host chip can read and
 * mutate the node's chosen format (see the flow message chip's format selector).
 */
export interface MergeTagChipRenderOptions {
  /** The node's current formatter id (e.g. `upper`, `decimal:2`), if any. */
  format?: string;
  /** Persist a new formatter id on the underlying node (empty string clears it). */
  updateFormat?: (format: string) => void;
  /** The node's current fallback (default) value, applied when the field is empty. */
  defaultValue?: string;
  /** Persist a new fallback value on the underlying node (empty string clears it). */
  updateDefault?: (defaultValue: string) => void;
}

/**
 * Renderer for merge-tag chips. `options` is optional so existing call sites
 * that only care about the path (`(path) => …`) keep working unchanged.
 */
export type MergeTagChipRenderer = (
  path: string,
  options?: MergeTagChipRenderOptions,
) => ReactNode;

/**
 * Optional renderer override for merge-tag chips. When a provider supplies one,
 * the node view delegates to it (passing the field path + format controls);
 * otherwise it falls back to the pure {@link DynamicFieldChip}.
 */
const MergeTagChipContext = createContext<MergeTagChipRenderer | null>(null);

/**
 * Resolve a field path to its display label. Shared so React chips (via
 * `renderChip`) and non-React surfaces that only need the text (e.g. the email
 * canvas preview) label the same path identically.
 */
const MergeTagLabelContext = createContext<((path: string) => string) | null>(null);

export function MergeTagChipProvider({
  renderChip,
  resolveLabel,
  children,
}: {
  renderChip: MergeTagChipRenderer;
  resolveLabel?: (path: string) => string;
  children: ReactNode;
}) {
  return (
    <MergeTagChipContext.Provider value={renderChip}>
      <MergeTagLabelContext.Provider value={resolveLabel ?? null}>
        {children}
      </MergeTagLabelContext.Provider>
    </MergeTagChipContext.Provider>
  );
}

/**
 * Read the injected merge-tag chip renderer, if any. Hosts (e.g. the flow email
 * builder) use this to decide whether to render dynamic fields as rich chips.
 */
export function useMergeTagChipRenderer(): MergeTagChipRenderer | null {
  return useContext(MergeTagChipContext);
}

/** Read the injected label resolver, if any (see {@link MergeTagChipProvider}). */
export function useMergeTagLabelResolver(): ((path: string) => string) | null {
  return useContext(MergeTagLabelContext);
}

/** Build the canonical inner `{ }` content from a node's attrs. */
function nodeInner(attrs: Record<string, unknown>): string {
  return buildMergeTagInner({
    path: (attrs.path as string) ?? "",
    format: (attrs.format as string) || undefined,
    defaultValue: (attrs.defaultValue as string) || undefined,
  });
}

function MergeTagNodeView({ node, updateAttributes }: NodeViewProps) {
  const path = (node.attrs.path as string) ?? "";
  const format = (node.attrs.format as string) ?? "";
  const defaultValue = (node.attrs.defaultValue as string) ?? "";
  const renderChip = useContext(MergeTagChipContext);
  const updateFormat = useCallback(
    (next: string) => updateAttributes({ format: next }),
    [updateAttributes],
  );
  const updateDefault = useCallback(
    (next: string) => updateAttributes({ defaultValue: next }),
    [updateAttributes],
  );
  return (
    <NodeViewWrapper as="span" className="inline-block align-baseline" contentEditable={false}>
      {renderChip ? (
        renderChip(path, { format, updateFormat, defaultValue, updateDefault })
      ) : (
        <DynamicFieldChip path={path} />
      )}
    </NodeViewWrapper>
  );
}

export const MergeTagNode = Node.create({
  name: "mergeTag",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      path: {
        default: "",
        // Parse the stored `data-path` through the token parser so legacy spans
        // whose `data-path` bundled a default/format (e.g. "path|default") still
        // resolve to a clean path.
        parseHTML: (element) => parseMergeTagInner(element.getAttribute("data-path") ?? "").path,
        renderHTML: (attributes) => ({ "data-path": attributes.path }),
      },
      format: {
        default: "",
        parseHTML: (element) => {
          const attr = element.getAttribute("data-format");
          if (attr) return attr;
          return parseMergeTagInner(element.getAttribute("data-path") ?? "").format ?? "";
        },
        renderHTML: (attributes) =>
          attributes.format ? { "data-format": attributes.format } : {},
      },
      defaultValue: {
        default: "",
        parseHTML: (element) => {
          const attr = element.getAttribute("data-default");
          if (attr) return attr;
          return parseMergeTagInner(element.getAttribute("data-path") ?? "").defaultValue ?? "";
        },
        renderHTML: (attributes) =>
          attributes.defaultValue ? { "data-default": attributes.defaultValue } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-merge-tag]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    // Serialize with the canonical `{path|…|format:id}` token as the span's text
    // content. HTML editors (e.g. the email block builder) store `getHTML()`
    // output verbatim, so the token must survive round-trips *and* stay visible
    // to the server's merge-tag replacement, which scans rendered text.
    return [
      "span",
      mergeAttributes(HTMLAttributes, { "data-merge-tag": "" }),
      `{${nodeInner(node.attrs)}}`,
    ];
  },

  renderText({ node }) {
    return `{${nodeInner(node.attrs)}}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(MergeTagNodeView);
  },
});

/**
 * Parse plain `{path}` merge-tag text into a ProseMirror doc, turning each token
 * into a {@link MergeTagNode} and preserving line breaks as paragraphs.
 */
export function mergeTagTextToDoc(text: string) {
  const lines = (text ?? "").split("\n");
  return {
    type: "doc",
    content: lines.map((line) => ({
      type: "paragraph",
      content: lineToInline(line),
    })),
  };
}

/**
 * Wrap bare `{path}` merge-tag tokens found in an HTML string with the
 * `<span data-merge-tag data-path>` markup that {@link MergeTagNode} parses into
 * a chip. Tokens already inside a merge-tag span are left untouched, so the
 * transform is idempotent and safe to run on `getHTML()` output.
 *
 * Runs in the browser only (uses `DOMParser`); callers guard for SSR by only
 * invoking it inside client-only editors.
 */
export function wrapMergeTagTokens(html: string): string {
  if (!html || !html.includes("{") || typeof window === "undefined") return html;

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  let changed = false;
  for (const textNode of textNodes) {
    const value = textNode.nodeValue ?? "";
    if (!value.includes("{")) continue;
    // Skip tokens already rendered as chips.
    if ((textNode.parentElement as HTMLElement | null)?.closest("[data-merge-tag]")) continue;

    TOKEN_REGEX.lastIndex = 0;
    if (!TOKEN_REGEX.test(value)) continue;

    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    TOKEN_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null = TOKEN_REGEX.exec(value);
    while (match !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(doc.createTextNode(value.slice(lastIndex, match.index)));
      }
      const parsed = parseMergeTagInner(match[1]);
      const span = doc.createElement("span");
      span.setAttribute("data-merge-tag", "");
      span.setAttribute("data-path", parsed.path);
      if (parsed.format) span.setAttribute("data-format", parsed.format);
      if (parsed.defaultValue) span.setAttribute("data-default", parsed.defaultValue);
      span.textContent = `{${buildMergeTagInner(parsed)}}`;
      fragment.appendChild(span);
      lastIndex = match.index + match[0].length;
      match = TOKEN_REGEX.exec(value);
    }
    if (lastIndex < value.length) {
      fragment.appendChild(doc.createTextNode(value.slice(lastIndex)));
    }
    textNode.replaceWith(fragment);
    changed = true;
  }

  return changed ? doc.body.innerHTML : html;
}

function lineToInline(line: string) {
  const nodes: Array<Record<string, unknown>> = [];
  let lastIndex = 0;
  TOKEN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null = TOKEN_REGEX.exec(line);
  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: "text", text: line.slice(lastIndex, match.index) });
    }
    const parsed = parseMergeTagInner(match[1]);
    nodes.push({
      type: "mergeTag",
      attrs: {
        path: parsed.path,
        format: parsed.format ?? "",
        defaultValue: parsed.defaultValue ?? "",
      },
    });
    lastIndex = match.index + match[0].length;
    match = TOKEN_REGEX.exec(line);
  }
  if (lastIndex < line.length) {
    nodes.push({ type: "text", text: line.slice(lastIndex) });
  }
  return nodes;
}
